import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Heart, AlertTriangle, CheckCircle2, Landmark, FileCheck2, Lock, Eye, Printer, Phone, Mail, BadgeCheck, IndianRupee } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import { useApp } from "../../context/AppContext";
import SEO from "../../components/SEO";
import Captcha from "../admin/Captcha";

const AMOUNTS = [500, 1000, 2500, 5000, 10000];
const TITLES = ["Mr.", "Mrs.", "Ms.", "Dr.", "Other"];
const PURPOSES = ["General Donation", "Education", "Healthcare", "Social Security", "Divyangjan Welfare", "Child Welfare", "Women Empowerment", "Emergency Relief", "Other"];
const STATES = ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"];
const TERMS = [
  "Donations are voluntary contributions to Sahaj Sarathi Foundation.",
  "A formal receipt will be generated only after successful payment confirmation.",
  "Donors are responsible for providing accurate information for receipt generation.",
  "PAN may be required where applicable under tax/compliance requirements.",
  "A failed, cancelled or incomplete transaction shall not be treated as a successful donation.",
  "If an amount is debited but confirmation is delayed, the donor should check payment status before initiating another transaction.",
  "Refund/cancellation, where legally applicable, will be governed by the Foundation's published Donation/Refund Policy.",
  "Donations and payment processing remain subject to applicable Indian law and payment-gateway rules.",
];

const inputCls = "w-full rounded-md border border-gray-300 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-saffron-500 focus:ring-2 focus:ring-saffron-500/30";
const labelCls = "mb-1.5 block text-sm font-semibold text-navy";

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
function inWords(n) {
  n = Math.round(Number(n) || 0);
  if (n <= 0) return "Zero";
  const two = (x) => (x < 20 ? ONES[x] : TENS[Math.floor(x / 10)] + (x % 10 ? " " + ONES[x % 10] : ""));
  const three = (x) => (x >= 100 ? ONES[Math.floor(x / 100)] + " Hundred" + (x % 100 ? " " : "") : "") + (x % 100 ? two(x % 100) : "");
  const parts = [];
  const crore = Math.floor(n / 1e7); n %= 1e7;
  const lakh = Math.floor(n / 1e5); n %= 1e5;
  const thou = Math.floor(n / 1e3); n %= 1e3;
  if (crore) parts.push(three(crore) + " Crore");
  if (lakh) parts.push(two(lakh) + " Lakh");
  if (thou) parts.push(two(thou) + " Thousand");
  if (n) parts.push(three(n));
  return parts.join(" ");
}
const maskPan = (p) => (p && p.length === 10 ? p.slice(0, 2) + "XXXXXX" + p.slice(-2) : "—");
const maskMobile = (m) => (m && m.length >= 10 ? `+91 XXXXX X${m.slice(-4)}` : m ? `+91 ${m}` : "—");

function Field({ label, required, children, testid, error }) {
  return (
    <div data-testid={testid}>
      <label className={labelCls}>{label}{required && <span className="text-red-500"> *</span>}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600" data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
}

function SidePanel({ s }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-gray-200 bg-white p-5" data-testid="panel-secure">
        <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-navy"><Lock size={14} className="text-brandgreen" /> Secure Donations</p>
        <ul className="mt-3 space-y-3 text-[13px] text-slate-600">
          <li className="flex gap-2"><ShieldCheck size={15} className="mt-0.5 shrink-0 text-brandgreen" /><span><b className="text-navy">Secure Payments</b><br />Powered by Cashfree</span></li>
          {(s.udyam || s.darpan_id) && (
            <li className="flex gap-2"><Landmark size={15} className="mt-0.5 shrink-0 text-brandgreen" /><span><b className="text-navy">Registered NGO</b><br />{[s.udyam && `UDYAM: ${s.udyam}`, s.darpan_id && `NITI Darpan: ${s.darpan_id}`].filter(Boolean).join(" · ")}</span></li>
          )}
          {s.tax_80g && (
            <li className="flex gap-2"><BadgeCheck size={15} className="mt-0.5 shrink-0 text-brandgreen" /><span><b className="text-navy">Tax Exemption</b><br />80G: {s.tax_80g}</span></li>
          )}
          <li className="flex gap-2"><FileCheck2 size={15} className="mt-0.5 shrink-0 text-brandgreen" /><span><b className="text-navy">Instant Receipt</b><br />Generated after confirmed successful payment.</span></li>
          <li className="flex gap-2"><Eye size={15} className="mt-0.5 shrink-0 text-brandgreen" /><span><b className="text-navy">Transparent Process</b><br />Your support makes a real difference</span></li>
        </ul>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-5" data-testid="panel-print-receipt">
        <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-navy"><Printer size={14} className="text-saffron-600" /> Print Receipt</p>
        <p className="mt-2 text-xs text-slate-500">Retrieve a receipt using your donation/receipt number and registered mobile or email.</p>
        <button onClick={() => navigate("/print-receipt")} className="btn-dark mt-3 w-full !rounded-md !py-2.5 text-xs" data-testid="panel-view-receipt">VIEW RECEIPT</button>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-5" data-testid="panel-help">
        <p className="text-sm font-bold uppercase tracking-wider text-navy">Need Help?</p>
        <div className="mt-3 space-y-2 text-[13px] text-slate-600">
          {s.phone && <p className="flex items-center gap-2"><Phone size={13} className="text-saffron-600" /> {s.phone}</p>}
          {s.email && <p className="flex items-center gap-2 break-all"><Mail size={13} className="shrink-0 text-saffron-600" /> {s.email}</p>}
          <button onClick={() => navigate("/donate/check-status")} className="mt-1 block text-xs font-bold text-saffron-600 hover:underline" data-testid="panel-check-status">Check Payment Status →</button>
          <button onClick={() => navigate("/contact")} className="mt-1 block text-xs font-bold text-saffron-600 hover:underline" data-testid="panel-contact">Contact Us →</button>
        </div>
      </div>
    </div>
  );
}

export default function Donate() {
  const { site } = useApp();
  const s = site?.settings || {};
  const [form, setForm] = useState({ title: "Mr.", purpose: "General Donation", state: "", country: "India", consent: false });
  const [chip, setChip] = useState(1000);
  const [custom, setCustom] = useState("");
  const [captcha, setCaptcha] = useState({ captcha_id: "", answer: "" });
  const [capKey, setCapKey] = useState(0);
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const finalAmount = custom ? Number(custom) : chip;
  const mobileDigits = (form.mobile || "").replace(/\D/g, "");
  const panOk = !form.pan || /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan);

  const valid = form.title && (form.name || "").trim().length >= 3
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email || "")
    && /^[6-9]\d{9}$/.test(mobileDigits.slice(-10))
    && finalAmount >= 10
    && (form.address || "").trim().length >= 5 && /^\d{6}$/.test(form.pin || "")
    && form.state && form.purpose && panOk
    && captcha.captcha_id && captcha.answer.trim() !== ""
    && form.consent;

  const openReview = (e) => {
    e.preventDefault();
    if (!valid) { toast.error("Please complete all required fields, verification code and the Terms checkbox."); return; }
    setStep("review");
    window.scrollTo({ top: 0 });
  };

  const proceedPayment = async () => {
    setBusy(true);
    try {
      const payload = { ...form, amount: finalAmount, captcha_id: captcha.captcha_id, captcha_answer: captcha.answer.trim() };
      if (s.payment_configured) {
        const r = await api.post("/payments/cashfree/create-order", payload);
        if (!r.data?.ok || !r.data?.payment_session_id) {
          toast.error("Could not start the payment — please try again.");
          return;
        }
        const { load } = await import("@cashfreepayments/cashfree-js");
        const cashfree = await load({ mode: r.data.mode || "production" });
        await cashfree.checkout({ paymentSessionId: r.data.payment_session_id, redirectTarget: "_self" });
        return; // browser redirects to Cashfree hosted checkout
      }
      const r = await api.post("/public/donations", payload);
      setDone(r.data);
      setStep("form");
    } catch (err) {
      toast.error(errMsg(err));
      setCapKey((k) => k + 1);
      setStep("form");
    } finally { setBusy(false); }
  };

  const reviewRows = [
    ["Name", `${form.title} ${form.name || ""}`],
    ["Email", form.email],
    ["PAN Number", maskPan(form.pan)],
    ["Donation Amount", `₹${(finalAmount || 0).toLocaleString("en-IN")} (${inWords(finalAmount)} Only)`],
    ["Address", form.address],
    ["State", form.state],
    ["PIN Code", form.pin],
    ["Country", "India"],
    ["Mobile Number", maskMobile(mobileDigits.slice(-10))],
    ["Purpose", form.purpose],
  ];

  return (
    <div className="bg-white" data-testid="donate-page">
      <SEO title="Domestic Donations" description="Donate securely to Sahaj Sarathi Foundation" />
      <section className="border-b border-gray-100 py-10">
        <div className="container-ssf max-w-5xl text-center">
          <h1 className="font-display text-3xl font-bold tracking-wide text-navy sm:text-4xl" data-testid="donate-heading">DOMESTIC DONATIONS</h1>
          <div className="mt-5 rounded-md border border-sky-200 bg-sky-50 px-5 py-3.5 text-left text-[13px] leading-relaxed text-navy" data-testid="donate-notice">
            <p><b>Important:</b> If your account is debited but payment confirmation is delayed, please do not immediately make another payment. Use <b>Check Payment Status</b> or contact Sahaj Sarathi Foundation.</p>
            <p className="mt-1.5">Please do not refresh, close the browser, or use the Back button while payment is being processed.</p>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="container-ssf max-w-5xl">
          {done ? (
            <div className="card-ssf p-10 text-center" data-testid="donation-result">
              {done.payment_configured ? (
                <>
                  <CheckCircle2 className="mx-auto h-12 w-12 text-brandgreen" strokeWidth={1.5} />
                  <h2 className="mt-4 font-display text-2xl font-bold text-navy">Donation Initiated</h2>
                  <p className="mt-2 text-sm text-slate-600">Reference: <b data-testid="donation-ref">{done.don_no}</b></p>
                </>
              ) : (
                <>
                  <AlertTriangle className="mx-auto h-12 w-12 text-saffron-600" strokeWidth={1.5} />
                  <h2 className="mt-4 font-display text-2xl font-bold text-navy">Payment Gateway Not Configured</h2>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600" data-testid="donation-notice">{done.message}</p>
                  <p className="mt-3 text-sm text-slate-600">Reference: <b data-testid="donation-ref">{done.don_no}</b></p>
                </>
              )}
            </div>
          ) : step === "review" ? (
            <div className="mx-auto max-w-2xl" data-testid="review-step">
              <div className="text-center">
                <h2 className="font-display text-2xl font-bold tracking-wide text-navy" data-testid="review-heading">REVIEW YOUR DONATION</h2>
                <p className="mt-1.5 text-sm text-slate-500">Please verify your details before proceeding to secure payment.</p>
              </div>
              <div className="card-ssf mt-6 overflow-hidden !p-0">
                <table className="w-full text-sm">
                  <tbody>
                    {reviewRows.map(([k, v], i) => (
                      <tr key={k} className={i % 2 ? "bg-navy-50/40" : "bg-white"}>
                        <td className="w-40 px-5 py-3 font-semibold text-navy">{k}</td>
                        <td className="px-5 py-3 text-slate-700" data-testid={`review-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button onClick={() => setStep("form")} disabled={busy} className="btn-dark flex-1 !rounded-md !py-3 text-sm disabled:opacity-50" data-testid="review-edit">EDIT DETAILS</button>
                <button onClick={proceedPayment} disabled={busy} className="btn-primary flex-1 !rounded-md !py-3 text-sm disabled:opacity-50" data-testid="review-proceed">
                  {busy ? "CONNECTING…" : "PROCEED TO SECURE PAYMENT"}
                </button>
              </div>
              <p className="mt-3 text-center text-[11px] text-slate-400">You will be redirected to the secure Cashfree payment page.</p>
            </div>
          ) : (
            <div className="grid gap-8 lg:grid-cols-[1fr_330px]">
              <form onSubmit={openReview} className="card-ssf p-6 sm:p-8" data-testid="donation-form">
                <p className="mb-5 border-b border-gray-100 pb-3 text-sm font-bold uppercase tracking-wider text-navy" data-testid="donor-details-heading">Donor Details</p>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Title" required testid="dn-f-title">
                    <select value={form.title} onChange={set("title")} className={inputCls} data-testid="dn-title">
                      {TITLES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Full Name" required testid="dn-f-name">
                    <input value={form.name || ""} onChange={set("name")} className={inputCls} data-testid="dn-name" autoComplete="name" />
                  </Field>
                  <Field label="Email" required testid="dn-f-email">
                    <input type="email" value={form.email || ""} onChange={set("email")} className={inputCls} data-testid="dn-email" autoComplete="email" />
                  </Field>
                  <Field label="Mobile Number" required testid="dn-f-mobile" error={form.mobile && !/^[6-9]\d{9}$/.test(mobileDigits.slice(-10)) ? "Enter a valid 10-digit Indian mobile number" : ""}>
                    <div className="flex">
                      <span className="flex items-center rounded-l-md border border-r-0 border-gray-300 bg-navy-50/60 px-3 text-sm font-semibold text-navy">+91</span>
                      <input inputMode="numeric" maxLength={10} value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} className={`${inputCls} rounded-l-none`} data-testid="dn-mobile" autoComplete="tel-national" />
                    </div>
                  </Field>
                  <Field label="PAN Number" testid="dn-f-pan" error={!panOk ? "PAN format: ABCDE1234F" : ""}>
                    <input value={form.pan || ""} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10) })} className={inputCls} data-testid="dn-pan" placeholder="Optional" />
                  </Field>
                  <Field label="Donation Amount (₹)" required testid="dn-f-amount">
                    <div className="flex flex-wrap gap-2">
                      {AMOUNTS.map((a) => (
                        <button type="button" key={a} onClick={() => { setChip(a); setCustom(""); }} data-testid={`amount-${a}`}
                          className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${!custom && chip === a ? "bg-saffron-500 text-navy shadow" : "border border-gray-300 bg-white text-navy hover:border-saffron-500"}`}>
                          ₹{a.toLocaleString("en-IN")}
                        </button>
                      ))}
                    </div>
                    <input type="number" min="10" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom Amount" className={`${inputCls} mt-2.5`} data-testid="amount-custom" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Address" required testid="dn-f-address">
                      <textarea rows={2} value={form.address || ""} onChange={set("address")} className={inputCls} data-testid="dn-address" autoComplete="street-address" />
                    </Field>
                  </div>
                  <Field label="PIN Code" required testid="dn-f-pin">
                    <input inputMode="numeric" maxLength={6} value={form.pin || ""} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })} className={inputCls} data-testid="dn-pin" autoComplete="postal-code" />
                  </Field>
                  <Field label="State" required testid="dn-f-state">
                    <select value={form.state} onChange={set("state")} className={inputCls} data-testid="dn-state">
                      <option value="">Please Select</option>
                      {STATES.map((st) => <option key={st}>{st}</option>)}
                    </select>
                  </Field>
                  <Field label="Country" testid="dn-f-country">
                    <input value="India" readOnly disabled className={`${inputCls} bg-navy-50/50 text-slate-500`} data-testid="dn-country" />
                  </Field>
                  <Field label="Purpose of Donation" required testid="dn-f-purpose">
                    <select value={form.purpose} onChange={set("purpose")} className={inputCls} data-testid="dn-purpose">
                      {PURPOSES.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </Field>
                </div>

                <div className="mt-6" data-testid="dn-f-captcha">
                  <p className={labelCls}>Verification Code <span className="text-red-500">*</span></p>
                  <Captcha key={capKey} onChange={setCaptcha} />
                </div>

                <div className="mt-6 rounded-md border border-gray-300" data-testid="terms-box">
                  <p className="border-b border-gray-300 bg-navy-50/50 px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-navy">Terms of Donation</p>
                  <ol className="list-decimal space-y-1.5 px-6 py-4 text-xs leading-relaxed text-slate-600 sm:px-8">
                    {TERMS.map((t, i) => <li key={i}>{t}</li>)}
                  </ol>
                </div>
                <label className="mt-4 flex items-start gap-2.5 text-sm text-slate-700">
                  <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 accent-saffron-500" data-testid="dn-consent" />
                  I have read and agree to the Terms of Donation/Contribution.
                </label>

                <button type="submit" disabled={!valid || busy}
                  className="btn-primary mt-6 w-full !rounded-md !py-3.5 text-sm tracking-wide disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid="donation-submit">
                  <Heart size={16} /> REVIEW &amp; PROCEED TO PAYMENT — ₹{(finalAmount || 0).toLocaleString("en-IN")}
                </button>
                <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
                  <ShieldCheck size={12} /> Secure payment via Cashfree. Payment status is always confirmed by the Foundation's secure server, never by your browser.
                </p>
              </form>
              <SidePanel s={s} />
            </div>
          )}

          <div className="mt-10 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="trust-strip">
            {[["Trusted NGO", BadgeCheck], ["Secure Payments", Lock], ["Transparent Process", Eye], ...(s.tax_80g ? [["Tax Benefits", IndianRupee]] : [["Instant Receipt", FileCheck2]])].map(([label, Icon]) => (
              <div key={label} className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-4 py-3.5">
                <Icon size={18} className="shrink-0 text-saffron-600" />
                <span className="text-[13px] font-bold text-navy">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
